import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Home,
  Add,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser } from '../../../hooks/useApi';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MarketGroupingDialog } from '../../../components/admin/MarketGroupingDialog';

interface MarketGrouping {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  market_count: number;
  created_at: string;
  updated_at: string;
}

export const MarketGroupings: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  // State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGrouping, setEditingGrouping] = useState<MarketGrouping | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupingToDelete, setGroupingToDelete] = useState<MarketGrouping | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch market groupings
  const { data: groupingsData, isLoading: groupingsLoading } = useQuery({
    queryKey: ['market-groupings-all'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/marketcompass/admin/market-groupings/list');
      if (!response || !response.ok) throw new Error('Failed to fetch market groupings');
      return response.json();
    },
    enabled: !!user
  });

  const groupings: MarketGrouping[] = groupingsData?.groupings || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (groupingId: number) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/market-groupings/${groupingId}/delete`,
        { method: 'DELETE' }
      );
      if (!response) {
        throw new Error('No response from server');
      }
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete grouping');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-groupings-all'] });
      setSuccess('Grouping deleted successfully');
      setDeleteDialogOpen(false);
      setGroupingToDelete(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  const handleAddNew = () => {
    setEditingGrouping(null);
    setDialogOpen(true);
  };

  const handleEdit = (grouping: MarketGrouping) => {
    setEditingGrouping(grouping);
    setDialogOpen(true);
  };

  const handleDelete = (grouping: MarketGrouping) => {
    setGroupingToDelete(grouping);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (groupingToDelete) {
      deleteMutation.mutate(groupingToDelete.id);
    }
  };

  const handleDialogClose = (success?: boolean) => {
    setDialogOpen(false);
    setEditingGrouping(null);
    if (success) {
      queryClient.invalidateQueries({ queryKey: ['market-groupings-all'] });
      setSuccess(editingGrouping ? 'Grouping updated successfully' : 'Grouping created successfully');
    }
  };

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 300
    },
    {
      field: 'market_count',
      headerName: 'Markets',
      width: 120,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 180,
      valueFormatter: (value: string) => {
        return new Date(value).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row as MarketGrouping)}
            title="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row as MarketGrouping)}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/market-compass/market-rankings');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Market Rankings
          </Link>
          <Typography color="text.primary">Market Groupings</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Group sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                Market Groupings
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Manage market groupings for rankings and analytics
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            size="large"
          >
            Add New Grouping
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Data Grid */}
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={groupings}
            columns={columns}
            loading={groupingsLoading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } }
            }}
            disableRowSelectionOnClick
          />
        </Paper>

        {/* Add/Edit Dialog */}
        <MarketGroupingDialog
          open={dialogOpen}
          grouping={editingGrouping}
          onClose={handleDialogClose}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the grouping "{groupingToDelete?.name}"?
              This will remove all markets from this grouping. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
